import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { GenericError } from "@via/components/error";
import Modal from "@via/components/modal.tsx";
import Table, { TableSize } from "@via/components/table/table";
import TableContextMenu from "@via/components/table/table-context-menu";
import {
  ERROR,
  ModalSize,
  USER_GROUP_MEMBERS_TABLE,
  USER_ROLES,
} from "@via/utils/constants";
import { TableContextMenuInterface } from "@via/utils/interfaces";
import { GetUserGroupsResponse, GetUserGroupMemberResponse } from "common";
import { type FC, useState } from "react";
import { useUserGroupMembers } from "@via/api/user-groups.api.tsx";
import AddUserGroupMember from "@via/pages/account/profile/user-groups/components/add-user-group-member";
import DeleteUserGroupMemberModal from "@via/pages/account/profile/user-groups/components/delete-user-group-member-modal";
import UserGroupMemberFilter from "@via/pages/account/profile/user-groups/components/user-group-member-filter";

type UserGroupMemberModalProps = {
  group: GetUserGroupsResponse;
  setToBeViewedGroup: (group: GetUserGroupsResponse | null) => void;
  userRole: string;
};

const columnHelper = createColumnHelper<GetUserGroupMemberResponse>();

const defaultParams = {
  sort: [{ id: "id", desc: true }],
  filters: [],
  page: 0,
  pageSize: 10,
};

const UserGroupMemberModal: FC<UserGroupMemberModalProps> = ({
  group,
  setToBeViewedGroup,
  userRole,
}) => {
  const [userToBeDeleted, setUserToBeDeleted] =
    useState<GetUserGroupMemberResponse | null>(null);
  const [page, setPage] = useState(defaultParams.page);
  const [pageSize, setPageSize] = useState(defaultParams.pageSize);
  const [sort, setSort] = useState<Array<{ id: string; desc: boolean }>>(
    defaultParams.sort
  );
  const [columnFilters, setColumnFilters] = useState<
    Array<{ id: string; value: unknown }>
  >(defaultParams.filters);

  const { data, isLoading, isError } = useUserGroupMembers({
    groupId: group.id,
    page,
    pageSize,
    sort,
    filter: columnFilters,
    includePending: true,
  });

  const handleAction = (
    option: TableContextMenuInterface,
    user: GetUserGroupMemberResponse
  ) => {
    switch (option.name) {
      case "Delete":
        setUserToBeDeleted(user);
        break;
      default:
        break;
    }
  };

  const columns = [
    columnHelper.accessor("name", {
      cell: info => info.getValue(),
      header: () => <span>Name</span>,
    }),
    columnHelper.accessor("userName", {
      cell: info => info.getValue(),
      header: () => <span>Username</span>,
    }),
    columnHelper.accessor("email", {
      cell: info => info.getValue(),
      header: () => <span>E-mail</span>,
    }),
    columnHelper.display({
      id: "status",
      header: () => <span>Status</span>,
      cell: ({ row }) =>
        row.original.isPending ? (
          <span className="rounded-full px-2 py-1 text-xs font-semibold border-1 border-gray-300">
            Pending
          </span>
        ) : (
          <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-800">
            Active
          </span>
        ),
    }),
  ] as Array<ColumnDef<GetUserGroupMemberResponse>>;

  if (group.own || userRole === USER_ROLES.ADMIN) {
    columns.push(
      columnHelper.accessor("id", {
        cell: info => (
          <TableContextMenu
            options={USER_GROUP_MEMBERS_TABLE}
            onSelect={option => handleAction(option, info.row.original)}
          />
        ),
        header: () => <span>Options</span>,
        enableSorting: false,
      }) as ColumnDef<GetUserGroupMemberResponse>
    );
  }

  return (
    <Modal
      size={ModalSize.Large}
      width={1000}
      open={!!group}
      setOpen={() => {
        setToBeViewedGroup(null);
      }}
      title="View User Group Members">
      {isError ? (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      ) : (
        <>
          {group.own || userRole === USER_ROLES.ADMIN ? (
            <AddUserGroupMember groupId={group.id} />
          ) : null}
          <Table
            isLoading={isLoading}
            loaderCount={6}
            data={data?.data || []}
            columns={columns}
            sort={sort}
            setSort={setSort}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            total={data?.total || 0}
            size={TableSize.Big}
            additionalFilters={
              <UserGroupMemberFilter
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                setPage={setPage}
                setPageSize={setPageSize}
              />
            }
          />
          {userToBeDeleted && (
            <DeleteUserGroupMemberModal
              groupId={group.id}
              user={userToBeDeleted}
              setUserToBeDeleted={setUserToBeDeleted}
              isPending={userToBeDeleted.isPending}
            />
          )}
        </>
      )}
    </Modal>
  );
};

export default UserGroupMemberModal;
